import { useEffect, useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Trash2, TrendingUp, TrendingDown, Save, RotateCcw, Mail, Newspaper, ExternalLink, CalendarClock } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid,
} from "recharts";

export default function StockDetail({ item, onClose, onUpdated, onDelete }) {
  const [history, setHistory] = useState(null);
  const [breakout, setBreakout] = useState("");
  const [support, setSupport] = useState("");
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [t3, setT3] = useState("");
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [newsAlerts, setNewsAlerts] = useState(true);
  const [news, setNews] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setBreakout(item.breakout_level?.toFixed(2) || "");
    setSupport(item.support_level?.toFixed(2) || "");
    setT1(item.target_1?.toFixed(2) || "");
    setT2(item.target_2?.toFixed(2) || "");
    setT3(item.target_3?.toFixed(2) || "");
    setEmailAlerts(item.email_alerts ?? true);
    setNewsAlerts(item.news_alerts ?? true);
    api.get(`/stocks/${item.symbol}/history`).then(r => setHistory(r.data)).catch(() => setHistory(null));
    api.get(`/stocks/${item.symbol}/news`).then(r => setNews(r.data || [])).catch(() => setNews([]));
  }, [item?.id]);

  if (!item) return null;
  const q = item.quote;
  const up = q && q.change >= 0;

  const save = async () => {
    setSaving(true);
    try {
      const bo = parseFloat(breakout);
      const sp = parseFloat(support);
      const nt1 = parseFloat(t1);
      const nt2 = parseFloat(t2);
      const nt3 = parseFloat(t3);
      const payload = {
        manual_breakout: isNaN(bo) ? null : bo,
        manual_support: isNaN(sp) ? null : sp,
        manual_target_1: isNaN(nt1) ? null : nt1,
        manual_target_2: isNaN(nt2) ? null : nt2,
        manual_target_3: isNaN(nt3) ? null : nt3,
        email_alerts: emailAlerts,
        news_alerts: newsAlerts,
      };
      await api.patch(`/watchlist/${item.id}`, payload);
      toast.success(`${item.symbol} updated`);
      onUpdated();
    } catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  const useAuto = () => {
    setBreakout(item.auto_high_20d?.toFixed(2) || "");
    setSupport(item.auto_low_20d?.toFixed(2) || "");
    setT1(item.auto_target_1?.toFixed(2) || "");
    setT2(item.auto_target_2?.toFixed(2) || "");
    setT3(item.auto_target_3?.toFixed(2) || "");
  };

  const chartData = (history?.rows || []).slice(0, 60).reverse().map(r => ({
    date: r.date.slice(5),
    close: r.close,
    volume: r.volume,
  }));

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-[#0B0F17] border-slate-800 text-slate-100 w-full sm:max-w-xl overflow-y-auto p-0" data-testid={`detail-${item.symbol}`}>
        <SheetHeader className="px-5 pt-5">
          <SheetTitle className="font-head text-2xl text-slate-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="font-head font-extrabold">{item.symbol}</span>
              {q && (
                <span className={`font-mono text-sm ${up ? "text-emerald-400" : "text-rose-400"} flex items-center gap-1`}>
                  {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {up ? "+" : ""}{q.change_percent.toFixed(2)}%
                </span>
              )}
            </div>
          </SheetTitle>
          {item.name && <div className="text-xs text-slate-500 -mt-1 text-left">{item.name}</div>}
        </SheetHeader>

        <div className="px-5 pb-5 pt-3 space-y-4">
          {q && (
            <div className="rounded-xl border border-slate-800 bg-[#121824] p-4">
              <div className="font-mono text-3xl font-bold">${q.price.toFixed(2)}</div>
              <div className={`font-mono text-xs mt-0.5 ${up ? "text-emerald-400" : "text-rose-400"}`}>
                {up ? "+" : ""}${q.change.toFixed(2)} today · Vol {(q.volume/1e6).toFixed(2)}M
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-800 font-mono text-[11px]">
                <div><div className="text-slate-500 text-[9px] uppercase tracking-wider">Open</div><div>${q.open.toFixed(2)}</div></div>
                <div><div className="text-slate-500 text-[9px] uppercase tracking-wider">Day High</div><div>${q.high.toFixed(2)}</div></div>
                <div><div className="text-slate-500 text-[9px] uppercase tracking-wider">Day Low</div><div>${q.low.toFixed(2)}</div></div>
              </div>
            </div>
          )}

          {item.earnings_date && (
            <div className={`rounded-xl border p-3 ${typeof item.earnings_days === "number" && item.earnings_days >= 0 && item.earnings_days <= 7 ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800 bg-[#121824]"}`} data-testid={`earnings-info-${item.symbol}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-amber-400" />
                  <div>
                    <div className="font-mono text-[10px] text-slate-400 uppercase tracking-wider">Next Earnings</div>
                    <div className="font-mono text-sm text-slate-100">
                      {item.earnings_date?.slice(0,10)}
                      {typeof item.earnings_days === "number" && (
                        <span className={`ml-2 text-xs ${item.earnings_days <= 7 ? "text-amber-400" : "text-slate-500"}`}>
                          {item.earnings_days === 0 ? "today" : item.earnings_days > 0 ? `in ${item.earnings_days}d` : `${Math.abs(item.earnings_days)}d ago`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {item.earnings_avg_eps != null && (
                  <div className="text-right font-mono">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Consensus EPS</div>
                    <div className="text-sm text-slate-200">${Number(item.earnings_avg_eps).toFixed(2)}</div>
                  </div>
                )}
              </div>
              {typeof item.earnings_days === "number" && item.earnings_days >= 0 && item.earnings_days <= 7 && (
                <div className="mt-2 text-[11px] text-amber-300/80 font-mono leading-relaxed">
                  Report within 7 days — tighten stops or consider trimming ahead of the volatility.
                </div>
              )}
            </div>
          )}

          {chartData.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-[#121824] p-3 pt-4">
              <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-1 px-1">60-day close · Breakout & Support bands</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData} margin={{ top: 5, right: 8, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#1E293B" }} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#1E293B" }} tickLine={false} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ background: "#121824", border: "1px solid #334155", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 11 }}
                    labelStyle={{ color: "#F8FAFC" }}
                  />
                  <Area type="monotone" dataKey="close" stroke="#818CF8" strokeWidth={1.5} fill="url(#grad)" />
                  {parseFloat(breakout) > 0 && (
                    <ReferenceLine y={parseFloat(breakout)} stroke="#10B981" strokeDasharray="4 4" label={{ value: `BO $${parseFloat(breakout).toFixed(2)}`, fill: "#10B981", fontSize: 10, fontFamily: "JetBrains Mono", position: "insideTopRight" }} />
                  )}
                  {parseFloat(support) > 0 && (
                    <ReferenceLine y={parseFloat(support)} stroke="#F43F5E" strokeDasharray="4 4" label={{ value: `SUP $${parseFloat(support).toFixed(2)}`, fill: "#F43F5E", fontSize: 10, fontFamily: "JetBrains Mono", position: "insideBottomRight" }} />
                  )}
                  {/* Resistance map: 50D, 52W, swing highs above breakout */}
                  {item.auto_high_50d && item.auto_high_50d > parseFloat(breakout) * 1.001 && (
                    <ReferenceLine y={item.auto_high_50d} stroke="#94A3B8" strokeDasharray="1 3" strokeOpacity={0.6} label={{ value: `50D $${item.auto_high_50d.toFixed(2)}`, fill: "#94A3B8", fontSize: 9, fontFamily: "JetBrains Mono", position: "insideLeft" }} />
                  )}
                  {item.high_52w && item.high_52w > parseFloat(breakout) * 1.001 && (
                    <ReferenceLine y={item.high_52w} stroke="#CBD5E1" strokeDasharray="1 3" strokeOpacity={0.75} label={{ value: `52W $${item.high_52w.toFixed(2)}`, fill: "#CBD5E1", fontSize: 9, fontFamily: "JetBrains Mono", position: "insideLeft" }} />
                  )}
                  {(item.swing_highs || []).slice(0, 3).filter(s => s.price > parseFloat(breakout) * 1.001).map((s, idx) => (
                    <ReferenceLine key={`sw-${idx}`} y={s.price} stroke="#64748B" strokeDasharray="1 5" strokeOpacity={0.5} label={{ value: `R ${s.price.toFixed(2)}`, fill: "#64748B", fontSize: 8, fontFamily: "JetBrains Mono", position: "insideLeft" }} />
                  ))}
                  {parseFloat(t1) > 0 && (
                    <ReferenceLine y={parseFloat(t1)} stroke="#F59E0B" strokeDasharray="2 4" label={{ value: `T1 $${parseFloat(t1).toFixed(2)}`, fill: "#F59E0B", fontSize: 9, fontFamily: "JetBrains Mono", position: "insideRight" }} />
                  )}
                  {parseFloat(t2) > 0 && (
                    <ReferenceLine y={parseFloat(t2)} stroke="#F59E0B" strokeDasharray="2 4" strokeOpacity={0.8} label={{ value: `T2 $${parseFloat(t2).toFixed(2)}`, fill: "#F59E0B", fontSize: 9, fontFamily: "JetBrains Mono", position: "insideRight" }} />
                  )}
                  {parseFloat(t3) > 0 && (
                    <ReferenceLine y={parseFloat(t3)} stroke="#F59E0B" strokeDasharray="2 4" strokeOpacity={0.6} label={{ value: `T3 $${parseFloat(t3).toFixed(2)}`, fill: "#F59E0B", fontSize: 9, fontFamily: "JetBrains Mono", position: "insideRight" }} />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="rounded-xl border border-slate-800 bg-[#121824] p-4 space-y-3">
            <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">Levels</div>
            <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
              <div>
                <div className="text-slate-500 text-[9px] uppercase tracking-wider mb-1">Auto 20D High</div>
                <div className="text-emerald-400">${item.auto_high_20d?.toFixed(2) || "—"}</div>
              </div>
              <div>
                <div className="text-slate-500 text-[9px] uppercase tracking-wider mb-1">Auto 20D Low</div>
                <div className="text-rose-400">${item.auto_low_20d?.toFixed(2) || "—"}</div>
              </div>
              <div>
                <div className="text-slate-500 text-[9px] uppercase tracking-wider mb-1">Auto 50D High</div>
                <div className="text-emerald-300/80">${item.auto_high_50d?.toFixed(2) || "—"}</div>
              </div>
              <div>
                <div className="text-slate-500 text-[9px] uppercase tracking-wider mb-1">Auto 50D Low</div>
                <div className="text-rose-300/80">${item.auto_low_50d?.toFixed(2) || "—"}</div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-800 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Breakout level</Label>
                <Input
                  value={breakout}
                  onChange={(e) => setBreakout(e.target.value)}
                  className="bg-[#0B0F17] border-slate-800 font-mono text-emerald-400 mt-1"
                  data-testid="breakout-input"
                  type="number"
                  step="0.01"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Support level</Label>
                <Input
                  value={support}
                  onChange={(e) => setSupport(e.target.value)}
                  className="bg-[#0B0F17] border-slate-800 font-mono text-rose-400 mt-1"
                  data-testid="support-input"
                  type="number"
                  step="0.01"
                />
              </div>
            </div>
            <div className="pt-3 border-t border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] text-amber-400/80 uppercase tracking-widest">Sell Targets · measured move</div>
                <div className="font-mono text-[9px] text-slate-500">Auto: {item.auto_target_1 ? `$${item.auto_target_1.toFixed(2)} / $${item.auto_target_2.toFixed(2)} / $${item.auto_target_3.toFixed(2)}` : "—"}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[{n:1, v:t1, set:setT1, hit: item.target_hit >= 1},
                  {n:2, v:t2, set:setT2, hit: item.target_hit >= 2},
                  {n:3, v:t3, set:setT3, hit: item.target_hit >= 3}].map(({n, v, set, hit}) => (
                  <div key={n}>
                    <Label className="text-[9px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                      Target {n} {hit && <span className="text-amber-400 font-mono">· HIT</span>}
                    </Label>
                    <Input
                      value={v}
                      onChange={(e) => set(e.target.value)}
                      className={`bg-[#0B0F17] border-slate-800 font-mono mt-1 ${hit ? "text-amber-300" : "text-amber-400"}`}
                      data-testid={`target-${n}-input`}
                      type="number"
                      step="0.01"
                      placeholder="—"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2 font-mono text-[9px] text-slate-500 leading-relaxed">
                Auto formula: picks the most conservative of 3 sources — measured move (BO + nR), ATR expansion (BO + n·ATR), and nearest resistance.
              </div>
              {(item.mm_targets || item.atr_targets || item.resistance_targets) && (
                <div className="mt-3 pt-3 border-t border-slate-800 space-y-1 font-mono text-[10px]">
                  <div className="text-slate-500 uppercase tracking-widest text-[9px] mb-1">Target sources</div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-slate-500">Source</div>
                    <div className="text-slate-500 text-right">T1</div>
                    <div className="text-slate-500 text-right">T2</div>
                    <div className="text-slate-500 text-right">T3</div>
                    <div className="text-slate-300">Measured</div>
                    <div className="text-right text-amber-300">${item.mm_targets?.[0]?.toFixed(2) || "—"}</div>
                    <div className="text-right text-amber-300">${item.mm_targets?.[1]?.toFixed(2) || "—"}</div>
                    <div className="text-right text-amber-300">${item.mm_targets?.[2]?.toFixed(2) || "—"}</div>
                    <div className="text-slate-300">ATR·{item.atr_14 ? `$${item.atr_14.toFixed(2)}` : "—"}</div>
                    <div className="text-right text-amber-300">{item.atr_targets?.[0] ? `$${item.atr_targets[0].toFixed(2)}` : "—"}</div>
                    <div className="text-right text-amber-300">{item.atr_targets?.[1] ? `$${item.atr_targets[1].toFixed(2)}` : "—"}</div>
                    <div className="text-right text-amber-300">{item.atr_targets?.[2] ? `$${item.atr_targets[2].toFixed(2)}` : "—"}</div>
                    <div className="text-slate-300">Resistance</div>
                    <div className="text-right text-slate-200">{item.resistance_targets?.[0] ? `$${item.resistance_targets[0].toFixed(2)}` : "—"}</div>
                    <div className="text-right text-slate-200">{item.resistance_targets?.[1] ? `$${item.resistance_targets[1].toFixed(2)}` : "—"}</div>
                    <div className="text-right text-slate-200">{item.resistance_targets?.[2] ? `$${item.resistance_targets[2].toFixed(2)}` : "—"}</div>
                  </div>
                </div>
              )}
              {item.vol_confirm && (
                <div className="mt-3 pt-3 border-t border-slate-800 flex items-start gap-2 font-mono text-[10px]" data-testid="confirmation-explainer">
                  <div className="flex-1">
                    <div className="text-slate-500 uppercase tracking-widest text-[9px] mb-1">Breakout Confirmation</div>
                    <div className={`text-xs ${item.vol_confirm === "confirmed" ? "text-emerald-400" : item.vol_confirm === "mild" ? "text-yellow-400" : "text-rose-400"}`}>
                      {item.vol_confirm === "confirmed" ? `● CONFIRMED (${item.last_vol_ratio}x volume on close)` :
                       item.vol_confirm === "mild" ? `● MILD (${item.last_vol_ratio}x volume)` :
                       `● WEAK (${item.last_vol_ratio}x volume) — fakeout risk`}
                    </div>
                    <div className="text-[9px] text-slate-500 mt-1 leading-relaxed">
                      A real breakout needs a daily close above the level with ≥1.5× average volume. Below 1.0× often fades back the next day.
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-sm text-slate-300 flex items-center gap-2"><Mail className="w-4 h-4" />Email alerts</Label>
              <Switch checked={emailAlerts} onCheckedChange={setEmailAlerts} data-testid="detail-email-switch" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-sm text-slate-300 flex items-center gap-2"><Newspaper className="w-4 h-4" />News alerts</Label>
              <Switch checked={newsAlerts} onCheckedChange={setNewsAlerts} data-testid="detail-news-switch" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={useAuto} variant="secondary" size="sm" className="flex-1 bg-slate-800 hover:bg-slate-700" data-testid="use-auto-btn">
                <RotateCcw className="w-3 h-3 mr-1" /> Use Auto 20D
              </Button>
              <Button onClick={save} disabled={saving} size="sm" className="flex-1 bg-indigo-500 hover:bg-indigo-600" data-testid="save-levels-btn">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-[#121824] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <Newspaper className="w-3.5 h-3.5" /> Recent News · Yahoo Finance
              </div>
              <span className="font-mono text-[9px] text-slate-500">{news.length} items</span>
            </div>
            {news.length === 0 ? (
              <div className="text-center text-slate-500 text-xs font-mono py-4">No news for {item.symbol} yet</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid={`news-feed-${item.symbol}`}>
                {news.slice(0, 15).map((a, i) => {
                  const sent = a.sentiment_label || "Neutral";
                  const sColor = sent === "Bullish" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
                    : sent === "Somewhat-Bullish" ? "text-emerald-300 border-emerald-500/20"
                    : sent === "Bearish" ? "text-rose-400 border-rose-500/40 bg-rose-500/5"
                    : sent === "Somewhat-Bearish" ? "text-rose-300 border-rose-500/20"
                    : "text-slate-400 border-slate-700";
                  return (
                    <a
                      key={a.url || i}
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg border border-slate-800 hover:border-slate-700 p-2.5 transition-colors group"
                      data-testid={`news-item-${i}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded border uppercase tracking-wider ${sColor}`}>
                              {sent}
                            </span>
                            <span className="font-mono text-[9px] text-slate-500 truncate">{a.source} · {a.published?.slice(0, 16) || ""}</span>
                          </div>
                          <div className="text-xs text-slate-200 leading-snug group-hover:text-white">{a.title}</div>
                          {a.summary && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{a.summary}</div>}
                        </div>
                        <ExternalLink className="w-3 h-3 text-slate-600 group-hover:text-slate-400 flex-shrink-0 mt-1" />
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <Button
            onClick={() => { onDelete(item); onClose(); }}
            variant="destructive"
            size="sm"
            className="w-full bg-rose-500/10 border border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
            data-testid="delete-detail-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Remove from watchlist
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
